import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_EDGE_THRESHOLD_PX = 1;
const AUTO_SCROLL_STEP_PX = 2;
const STEP_SCROLL_RATIO = 0.6;

type ScrollDirection = 'left' | 'right';

/**
 * Tracks whether a horizontally scrollable element can scroll further left or
 * right, and exposes helpers to scroll it in either direction (a single step,
 * or a continuous auto-scroll while the user hovers/holds a control).
 */
export function useHorizontalScrollArrows(
  scrollRef: RefObject<HTMLElement | null>,
) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const autoScrollFrameRef = useRef<number | null>(null);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    setCanScrollLeft(el.scrollLeft > SCROLL_EDGE_THRESHOLD_PX);
    setCanScrollRight(
      el.scrollLeft <
        el.scrollWidth - el.clientWidth - SCROLL_EDGE_THRESHOLD_PX,
    );
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    updateScrollState();
    el.addEventListener(`scroll`, updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    window.addEventListener(`resize`, updateScrollState);

    return () => {
      el.removeEventListener(`scroll`, updateScrollState);
      resizeObserver.disconnect();
      window.removeEventListener(`resize`, updateScrollState);
    };
  }, [scrollRef, updateScrollState]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(
    (direction: ScrollDirection) => {
      stopAutoScroll();

      const step = () => {
        const el = scrollRef.current;
        if (!el) {
          return;
        }

        el.scrollLeft +=
          direction === `left` ? -AUTO_SCROLL_STEP_PX : AUTO_SCROLL_STEP_PX;
        autoScrollFrameRef.current = requestAnimationFrame(step);
      };

      autoScrollFrameRef.current = requestAnimationFrame(step);
    },
    [scrollRef, stopAutoScroll],
  );

  const scrollStep = useCallback(
    (direction: ScrollDirection) => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }

      const amount = el.clientWidth * STEP_SCROLL_RATIO;
      el.scrollBy({
        left: direction === `left` ? -amount : amount,
        behavior: `smooth`,
      });
    },
    [scrollRef],
  );

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  return {
    canScrollLeft,
    canScrollRight,
    scrollStep,
    startAutoScroll,
    stopAutoScroll,
  };
}
